import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useExpenseDetail, useUpdateExpense } from '@/hooks/useExpenses';
import { useGroupDetail } from '@/hooks/useGroups';
import { canModifyExpense } from '@/lib/expensePermissions';
import { useCategories } from '@/hooks/useCategories';
import { useAuth } from '@/contexts/AuthContext';
import { SPLIT_TYPE_LABELS, fromMinorUnits, getMinorUnitsSync } from '@sharecost/shared';
import type { SplitType } from '@sharecost/shared';
import { Loader2, ArrowLeft } from 'lucide-react';
import { FieldError } from '@/components/common/FormHelpers';
import { SplitMembersSection } from '@/components/expenses/SplitMembersSection';
import { composeExpenseTimestamp } from '@/lib/dateTime';
import { useTranslation } from 'react-i18next';

export function EditExpensePage() {
  const { t } = useTranslation();
  const { id: expenseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: expense, isLoading } = useExpenseDetail(expenseId);
  const { data: group, isLoading: isGroupLoading } = useGroupDetail(expense?.group_id);
  const { data: categories } = useCategories();
  const updateExpense = useUpdateExpense();

  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [paidBy, setPaidBy] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [date, setDate] = useState('');
  const [initialDate, setInitialDate] = useState('');
  const [splitType, setSplitType] = useState<SplitType>('equal');
  const [splitWith, setSplitWith] = useState<string[]>([]);
  const [exactAmounts, setExactAmounts] = useState<Record<string, string>>({});
  const [percentages, setPercentages] = useState<Record<string, string>>({});
  const [shares, setShares] = useState<Record<string, string>>({});
  const [initialized, setInitialized] = useState(false);
  const [tried, setTried] = useState(false);

  const parsedAmount = parseFloat(amount);

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!description.trim()) e.description = 'Description is required';
    if (!amount) e.amount = 'Amount is required';
    else if (isNaN(parsedAmount) || parsedAmount <= 0) e.amount = 'Enter a valid positive amount';
    if (splitWith.length === 0) e.splitWith = 'Select at least one member';

    if (splitType === 'exact' && splitWith.length > 0 && !isNaN(parsedAmount) && parsedAmount > 0) {
      const sum = splitWith.reduce((s, id) => s + (parseFloat(exactAmounts[id]) || 0), 0);
      const diff = Math.abs(sum - parsedAmount);
      if (diff > 0.01) e.split = `Exact amounts sum to ${sum.toFixed(2)} but total is ${parsedAmount.toFixed(2)}`;
    }
    if (splitType === 'percentage' && splitWith.length > 0) {
      const sum = splitWith.reduce((s, id) => s + (parseFloat(percentages[id]) || 0), 0);
      if (Math.abs(sum - 100) > 0.1) e.split = `Percentages sum to ${sum.toFixed(1)}% — must be 100%`;
    }
    return e;
  }, [description, amount, parsedAmount, splitWith, splitType, exactAmounts, percentages]);

  const hasErrors = Object.keys(errors).length > 0;

  // Pre-fill form from existing expense
  useEffect(() => {
    if (expense && !initialized) {
      const minorUnits = getMinorUnitsSync(expense.currency_code_src);
      setDescription(expense.description);
      setAmount(fromMinorUnits(Number(expense.amount_minor_src), minorUnits).toString());
      setPaidBy(expense.paid_by);
      setCategoryId(expense.category_id ?? '');
      const prefilledDate = expense.expense_date?.split('T')[0] ?? '';
      setDate(prefilledDate);
      setInitialDate(prefilledDate);
      const st = expense.split_type ?? 'equal';
      setSplitType(st);
      setSplitWith(expense.splits?.map(s => s.user_id) ?? []);

      // Pre-fill per-user split values from existing data
      if (st === 'exact') {
        const ea: Record<string, string> = {};
        for (const s of expense.splits ?? []) {
          ea[s.user_id] = (s.split_value ?? 0).toString();
        }
        setExactAmounts(ea);
      } else if (st === 'percentage') {
        const pct: Record<string, string> = {};
        for (const s of expense.splits ?? []) {
          pct[s.user_id] = (s.split_value ?? 0).toString();
        }
        setPercentages(pct);
      } else if (st === 'shares') {
        const sh: Record<string, string> = {};
        for (const s of expense.splits ?? []) {
          sh[s.user_id] = (s.split_value ?? 1).toString();
        }
        setShares(sh);
      }

      setInitialized(true);
    }
  }, [expense, initialized]);

  if (isLoading || !expense) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  const canEdit = canModifyExpense(expense, user?.id, group);

  // Admin rights come from the group; wait for it before refusing access
  if (!canEdit && isGroupLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="text-center py-12">
        <p className="text-text-tertiary">{t('web:editExpensePage.youCanOnlyEditExpensesYou')}</p>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (hasErrors || !expense || !user) return;

    // Only send `date` when the user actually changed the calendar day.
    // Unchanged dates preserve the original timestamp (including time-of-day)
    // so sort order stays stable across edits.
    const dateUpdate = date && date !== initialDate
      ? composeExpenseTimestamp(date)
      : undefined;

    try {
      await updateExpense.mutateAsync({
        expenseId: expense.id,
        groupId: expense.group_id,
        updates: {
          description,
          amount: parsedAmount,
          paidBy,
          categoryId: categoryId || undefined,
          date: dateUpdate,
          splitType,
          splitWith,
          exactAmounts: splitType === 'exact'
            ? Object.fromEntries(splitWith.map(id => [id, parseFloat(exactAmounts[id]) || 0]))
            : undefined,
          percentages: splitType === 'percentage'
            ? Object.fromEntries(splitWith.map(id => [id, parseFloat(percentages[id]) || 0]))
            : undefined,
          shares: splitType === 'shares'
            ? Object.fromEntries(splitWith.map(id => [id, parseFloat(shares[id]) || 1]))
            : undefined,
        },
      });
      navigate(-1);
    } catch {
      // Error shown via mutation
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 flex items-center gap-1 text-sm text-text-tertiary hover:text-text-secondary"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('common:button.back')}
      </button>

      <h1 className="text-2xl font-bold text-text-primary mb-6">{t('expenses:screen.editExpense')}</h1>

      {updateExpense.error && (
        <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
          {updateExpense.error instanceof Error ? updateExpense.error.message : t('web:editExpensePage.failedToUpdateExpense')}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5 rounded-xl bg-surface-primary p-6 shadow-sm ring-1 ring-border-primary">
        <div>
          <label className="block text-sm font-medium text-text-secondary">{t('expenses:form.description.label')}</label>
          <input
            type="text"
            value={description}
            onChange={e => setDescription(e.target.value)}
            className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 ${
              tried && errors.description
                ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                : 'border-border-secondary focus:border-brand-500'
            }`}
          />
          {tried && <FieldError message={errors.description} />}
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary">
            {t('web:common.amount', { currency: expense.currency_code_src })}
          </label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 ${
              tried && errors.amount
                ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                : 'border-border-secondary focus:border-brand-500'
            }`}
          />
          {tried && <FieldError message={errors.amount} />}
        </div>

        {group && (
          <>
            <div>
              <label className="block text-sm font-medium text-text-secondary">{t('expenses:detail.paidBy')}</label>
              <select
                value={paidBy}
                onChange={e => setPaidBy(e.target.value)}
                className="mt-1 w-full rounded-lg border border-border-secondary pl-3 pr-10 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              >
                {group.members.map(m => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.user?.display_name}{m.user_id === user?.id ? ` ${t('web:common.youSuffix')}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-text-secondary">{t('expenses:form.category.label')}</label>
                <select
                  value={categoryId}
                  onChange={e => setCategoryId(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-border-secondary pl-3 pr-10 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                >
                  <option value="">{t('web:common.noCategory')}</option>
                  {categories?.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary">{t('settlements:view.date')}</label>
                <input
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-border-secondary px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">{t('expenses:detail.splitType')}</label>
              <div className="flex gap-2 flex-wrap">
                {(Object.entries(SPLIT_TYPE_LABELS) as [SplitType, string][]).map(([type, label]) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setSplitType(type)}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                      splitType === type
                        ? 'bg-brand-600 text-white'
                        : 'bg-surface-tertiary text-text-secondary hover:bg-surface-inset'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <SplitMembersSection
              members={group.members}
              currentUserId={user?.id}
              splitType={splitType}
              splitWith={splitWith}
              onSplitWithChange={setSplitWith}
              totalAmount={parsedAmount}
              exactAmounts={exactAmounts}
              onExactAmountsChange={setExactAmounts}
              percentages={percentages}
              onPercentagesChange={setPercentages}
              shares={shares}
              onSharesChange={setShares}
              showErrors={tried}
              splitWithError={errors.splitWith}
              splitError={errors.split}
            />
          </>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 rounded-lg border border-border-secondary px-4 py-2.5 text-sm font-medium text-text-secondary hover:bg-surface-hover"
          >
            {t('common:button.cancel')}
          </button>
          <button
            type="submit"
            disabled={updateExpense.isPending || (tried && hasErrors)}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {updateExpense.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('expenses:form.save')}
          </button>
        </div>
      </form>
    </div>
  );
}
